import { useCallback, useRef, useState } from 'react';

/**
 * Which checklist row is being recorded, and what happens when somebody opens another (PG15, PG9).
 *
 * Julio approved PG15 on 2026-10-06: "Record" on a checklist row stops opening a dialog and opens
 * the form in the page under that row — because the form is five fields at its smallest and
 * fourteen or fifteen for a credential, and PG10 puts anything over five in the page.
 *
 * WHY THIS IS NOT STATE INSIDE THE ROW. Each row used to own its own `open`, which is all a dialog
 * needs. PG15 adds two rules a row cannot enforce alone: only ONE form is open at a time, and
 * opening a second row while the first has typing must ask "Discard what you typed?" first. Both
 * are statements about the rows as a set, so they live above the rows.
 *
 * THE OPEN FORM REPORTS ITS OWN DIRTINESS through a registered callback rather than this hook
 * tracking fields. The form has fourteen or fifteen of them, several nested, and a copy kept here
 * would be a second source of truth that drifts the first time somebody adds a field — the same
 * failure the `PopupDialog` discard flow avoids by keeping the form itself rather than its values.
 */
export interface RecordingRow {
  /** The row whose form is open, or null. */
  readonly openId: string | null;
  /** The row a discard prompt is deciding about, or null when nothing is being asked. */
  readonly askingFor: string | null;
  /** Open this row's form, asking first when the open one has typing. */
  readonly requestOpen: (id: string) => void;
  /** Close the open form, asking first when it has typing. */
  readonly requestClose: () => void;
  /** Throw away what was typed and go where the person was heading. */
  readonly discard: () => void;
  /** Leave the prompt and stay where they were. */
  readonly keepEditing: () => void;
  /** The open form calls this so the switch above knows whether to ask. */
  readonly registerDirty: (isDirty: () => boolean) => void;
}

export function useRecordingRow(): RecordingRow {
  const [openId, setOpenId] = useState<string | null>(null);
  const [askingFor, setAskingFor] = useState<string | null>(null);
  const dirty = useRef<(() => boolean) | null>(null);

  /**
   * Cleared as the form goes, and ONLY then. Leaving a stale reporter behind would make the next
   * row inherit the last one's typing and ask about a form nobody is looking at.
   */
  const leave = useCallback((next: string | null) => {
    dirty.current = null;
    setAskingFor(null);
    setOpenId(next);
  }, []);

  const requestOpen = useCallback((id: string) => {
    // Pressing Record on the row already open is not a switch; it changes nothing rather than
    // asking the person whether to discard the thing they are still typing into.
    if (id === openId) return;
    if (openId !== null && dirty.current?.() === true) { setAskingFor(id); return; }
    leave(id);
  }, [openId, leave]);

  const requestClose = useCallback(() => {
    if (openId === null) return;
    if (dirty.current?.() === true) { setAskingFor(CLOSING); return; }
    leave(null);
  }, [openId, leave]);

  const discard = useCallback(() => {
    setAskingFor((pending) => {
      leave(pending === CLOSING || pending === null ? null : pending);
      return null;
    });
  }, [leave]);

  const keepEditing = useCallback(() => setAskingFor(null), []);

  const registerDirty = useCallback((isDirty: () => boolean) => { dirty.current = isDirty; }, []);

  return { openId, askingFor, requestOpen, requestClose, discard, keepEditing, registerDirty };
}

/**
 * The prompt needs to tell "they asked to close this" from "they asked to open that one", and a
 * row id can never be this, because it is not a row.
 */
export const CLOSING = '\u0000closing';
